/* eslint-disable @typescript-eslint/naming-convention */
import { useNavBar } from './useNavBar'
import { Content } from './Content'
import LogoImage from 'assets/logo.svg'
import { useLayout } from 'hooks/useLayout'
import { type NavBarRouteDef } from 'types/nav'
import { colors } from 'components/ThemeProvider'
import { APP_BAR_HEIGHT } from 'components/AppBar/AppBar'
import { Box, Divider, Drawer, SvgIcon } from '@mui/material'
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined'
import ContestsIcon from 'assets/nav_icons/contests.svg?react'
import DashboardIcon from 'assets/nav_icons/dashboard.svg?react'
import PromotionsIcon from 'assets/nav_icons/promotions.svg?react'
import CommunicationsIcon from 'assets/nav_icons/communications.svg?react'
import RewardsProgramIcon from 'assets/nav_icons/rewards_program.svg?react'
import PatronAccountsIcon from 'assets/nav_icons/patron_accounts.svg?react'
import ClientManagementIcon from 'assets/nav_icons/client_management.svg?react'
import SettingsIcon from 'assets/nav_icons/settings.svg?react'
import { DeployVersion } from 'components/DeployVersion'
import { useSideNavCollapseStore } from 'stores/useSideNavCollapseStore'
import React from 'react'
import { featureAccessStore } from 'stores/featureAccessStore'
import { States } from 'stores/useCurrentStateStore'
import { type groupPermissionStore } from 'stores/groupPermissionStore'

type GroupPermissionState = ReturnType<typeof groupPermissionStore.getState>

const navIconSx = {
  color: colors.text.primary,
  transform: 'translate(0, -2px)',
}

const resolveIconComponent = (iconSource: unknown): React.ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: React.ElementType }).default
  }

  return iconSource as React.ElementType
}

const createNavIcon = (iconSource: unknown) => {
  const IconComponent = resolveIconComponent(iconSource)
  if (!IconComponent) {
    return null
  }

  return (
    <SvgIcon sx={navIconSx}>
      <IconComponent />
    </SvgIcon>
  )
}

export const getNavBarRoutes = (
  currentStateName: string,
  permissions: GroupPermissionState
): Record<string, NavBarRouteDef> => {
  const menuItemAllowed = currentStateName === States.ILLINOIS

  const homePermission = permissions.homePermission
  const clientManagementPermission = permissions.clientManagementPermission
  const clientManagement_LicensedEstablishmentsPermission =
    permissions.clientManagement_LicensedEstablishmentsPermission
  const clientManagement_OrganizationsPermission =
    permissions.clientManagement_OrganizationsPermission
  const clientManagement_CorporateAccountsPermission =
    permissions.clientManagement_CorporateAccountsPermission
  const clientManagement_LEConnectUsersPermission =
    permissions.clientManagement_LEConnectUsersPermission
  const patronAccountsPermission = permissions.patronAccountsPermission
  const rewardsPermission = permissions.rewardsPermission
  const rewards_RewardProgramsPermission =
    permissions.rewards_RewardProgramsPermission
  const rewards_RewardCatalogsPermission =
    permissions.rewards_RewardCatalogsPermission
  const rewards_RewardItemsPermission =
    permissions.rewards_RewardItemsPermission
  const promotionsPermission = permissions.promotionsPermission
  const contestsPermission = permissions.contestsPermission
  const contests_SweepstakesPermission =
    permissions.contests_SweepstakesPermission
  const contests_LocationSweepstakesPermission =
    permissions.contests_LocationSweepstakesPermission
  const contests_PassportGamesPermission =
    permissions.contests_PassportGamesPermission
  const communicationsPermission = permissions.communicationsPermission
  const communications_MessagesPermission =
    permissions.communications_MessagesPermission
  const communications_BannersPermission =
    permissions.communications_BannersPermission
  const jjUpdatesPermission = permissions.jjUpdatesPermission
  const settingsPermission = permissions.settingsPermission
  const contests_BadgeRewardsPermission =
    permissions.contests_BadgeRewardsPermission
  const contests_LocationsBadgeRewardsPermission =
    permissions.contests_LocationsBadgeRewardsPermission

  const specialsAndEventsFeatureAllowed =
    featureAccessStore.getState().specialsAndEvents
  const communicationsBannerMessagesFeatureAllowed =
    featureAccessStore.getState().communicationsBannerMessages
  const communicationsPushNotificationsFeatureAllowed =
    featureAccessStore.getState().communicationsPushNotifications
  const noRewardsFeatureAllowed = featureAccessStore.getState().noRewards
  const noSweepstakesFeatureAllowed =
    featureAccessStore.getState().noSweepstakes
  const jjSweepstakesFeatureAllowed =
    featureAccessStore.getState().sweepstakesJJ
  const locationSweepstakesFeatureAllowed =
    featureAccessStore.getState().sweepstakesLocation

  return {
    ...(homePermission && {
      Dashboard: {
        path: '/Home',
        icon: createNavIcon(DashboardIcon),
      },
    }),

    ...(clientManagementPermission && {
      'Client Management': {
        path: '/ClientManagement',
        icon: createNavIcon(ClientManagementIcon),
        childrenRoutes: [
          ...(clientManagement_LicensedEstablishmentsPermission
            ? [
                {
                  title: 'Licensed Establishments',
                  path: '/ClientManagement/LicensedEstablishments' as const,
                  icon: null,
                  associatedPageUrls: [
                    '/ClientManagement/LicensedEstablishments/New',
                    '/ClientManagement/LicensedEstablishments/',
                  ],
                },
              ]
            : []),
          ...(clientManagement_OrganizationsPermission
            ? [
                {
                  title: 'Organizations',
                  path: '/ClientManagement/Organizations' as const,
                  icon: null,
                  associatedPageUrls: [
                    '/ClientManagement/Organizations/New',
                    '/ClientManagement/Organizations/',
                  ],
                },
              ]
            : []),
          ...(clientManagement_CorporateAccountsPermission
            ? [
                {
                  title: 'Corporate Accounts',
                  path: '/ClientManagement/CorporateAccounts' as const,
                  icon: null,
                  associatedPageUrls: ['/ClientManagement/CorporateAccounts/'],
                },
              ]
            : []),
          ...(clientManagement_LEConnectUsersPermission
            ? [
                {
                  title: 'J&J Connect Users',
                  path: '/ClientManagement/LEConnectUsers' as const,
                  icon: null,
                  associatedPageUrls: ['/ClientManagement/LEConnectUsers/New'],
                },
              ]
            : []),
        ],
      },
    }),

    ...(patronAccountsPermission && {
      'Patron Accounts': {
        path: '/PatronAccounts',
        icon: createNavIcon(PatronAccountsIcon),
      },
    }),

    ...(!noRewardsFeatureAllowed &&
      rewardsPermission && {
        Rewards: {
          path: '/Rewards' as const,
          icon: createNavIcon(RewardsProgramIcon),
          childrenRoutes: [
            ...(rewards_RewardProgramsPermission
              ? [
                  {
                    title: 'Reward Programs',
                    path: '/Rewards/RewardPrograms' as const,
                    icon: null,
                    associatedPageUrls: ['/Rewards/RewardPrograms/'],
                  },
                ]
              : []),
            ...(rewards_RewardCatalogsPermission
              ? [
                  {
                    title: 'Reward Catalogs',
                    path: '/Rewards/RewardCatalogs' as const,
                    icon: null,
                    associatedPageUrls: ['/Rewards/RewardCatalogs'],
                  },
                ]
              : []),
            ...(rewards_RewardItemsPermission
              ? [
                  {
                    title: 'Reward Items',
                    path: '/Rewards/RewardItems' as const,
                    icon: null,
                    associatedPageUrls: ['/Rewards/RewardItems'],
                  },
                ]
              : []),
          ],
        },
      }),

    ...(specialsAndEventsFeatureAllowed &&
      promotionsPermission && {
        'Specials & Events': {
          path: '/Promotions',
          icon: createNavIcon(PromotionsIcon),
        },
      }),

    ...(!noSweepstakesFeatureAllowed &&
      contestsPermission && {
        Contests: {
          path: '/Contests',
          icon: createNavIcon(ContestsIcon),
          childrenRoutes: [
            ...(locationSweepstakesFeatureAllowed &&
            contests_SweepstakesPermission
              ? [
                  {
                    title: 'J&J Sweepstakes',
                    path: '/Contests/Sweepstakes' as const,
                    icon: null,
                    associatedPageUrls: ['/Contests/Sweepstakes/'],
                  },
                ]
              : []),
            ...(jjSweepstakesFeatureAllowed &&
            contests_LocationSweepstakesPermission
              ? [
                  {
                    title: 'Location Sweepstakes',
                    path: '/Contests/LocationSweepstakes' as const,
                    icon: null,
                    associatedPageUrls: ['/Contests/LocationSweepstakes/'],
                  },
                ]
              : []),
            ...(menuItemAllowed && contests_PassportGamesPermission
              ? [
                  {
                    title: 'Passport Games',
                    path: '/Contests/PassportGames' as const,
                    icon: null,
                    associatedPageUrls: ['/Contests/PassportGames/'],
                  },
                ]
              : []),
            ...(contests_BadgeRewardsPermission
              ? [
                  {
                    title: 'J&J Badges',
                    path: '/Contests/JJBadges' as const,
                    icon: null,
                    associatedPageUrls: ['/Contests/JJBadges/'],
                  },
                ]
              : []),
            ...(contests_BadgeRewardsPermission &&
            contests_LocationsBadgeRewardsPermission
              ? [
                  {
                    title: 'Location Badges',
                    path: '/Contests/LocationBadges' as const,
                    icon: null,
                    associatedPageUrls: ['/Contests/LocationBadges/'],
                  },
                ]
              : []),
          ],
        },
      }),

    ...(communicationsPermission && {
      Communications: {
        path: '/Communications',
        icon: createNavIcon(CommunicationsIcon),
        childrenRoutes: [
          ...(communicationsPushNotificationsFeatureAllowed &&
          communications_MessagesPermission
            ? [
                {
                  title: 'Messages',
                  path: '/Communications/Messages' as const,
                  icon: null,
                  associatedPageUrls: ['/Communications/Messages/'],
                },
              ]
            : []),

          ...(communicationsBannerMessagesFeatureAllowed &&
          communications_BannersPermission
            ? [
                {
                  title: 'Banners',
                  path: '/Communications/Banners' as const,
                  icon: null,
                  associatedPageUrls: ['/Communications/Banners/'],
                },
              ]
            : []),
        ],
      },
    }),

    ...(menuItemAllowed &&
      jjUpdatesPermission && {
        'J&J Updates': {
          path: '/JJUpdates',
          icon: createNavIcon(FeedOutlinedIcon),
        },
      }),
    ...(menuItemAllowed &&
      settingsPermission && {
        Settings: {
          path: '/Settings/EULA',
          icon: createNavIcon(SettingsIcon),
        },
      }),
  }
}

export const NAVIGATION_BAR_WIDTH = 261

export const NavigationBar = () => {
  const isOpen = useNavBar((state) => state.isOpen)
  const close = useNavBar((state) => state.close)
  const { isMobile } = useLayout()

  const setActiveTab = useSideNavCollapseStore((state) => state.setActiveTab)

  React.useEffect(() => {
    setActiveTab(location.pathname)
  }, [location.pathname, setActiveTab])

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        PaperProps={{
          sx: { top: 0 },
        }}
        sx={{
          display: {
            xs: 'block',
            sm: 'block',
            md: 'block',
            lg: 'none',
          },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isMobile
              ? `${NAVIGATION_BAR_WIDTH * 1.3}px`
              : `${NAVIGATION_BAR_WIDTH}px`,
          },
        }}
        open={isOpen}
        onClose={close}
        data-testid="mobile-navigation-bar"
      >
        <Box m={1} sx={{ ml: 2 }}>
          <img src={LogoImage} width={230} alt="LogoImage" />
        </Box>
        <Divider />
        <Content />
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: { top: `${APP_BAR_HEIGHT}px` },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: `${NAVIGATION_BAR_WIDTH}px`,
          height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
          overflowX: 'hidden',
        },
      }}
      open={true}
      data-testid="desktop-navigation-bar"
    >
      <Content />
      <DeployVersion />
    </Drawer>
  )
}
